import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { SwalService } from '../../../../common/services/swal.service';
import { InsuranceTypesService } from '../../services/insurance-types.service';
import { InsuranceTypeDto } from '../../../../core/data/new-dtos/insurance-type.dtos';
import { TableComponent } from '../../../../common/components/table/table.component';
import { TableColumnDirective } from '../../../../common/directives/table-column.directive';
import { TableContainerComponent } from '../../../../common/components/table-container/table-container.component';
import { SharedModule } from '../../../../common/shared.module';
import TableHeader from '../../../../common/interfaces/table/tableHeader';
import { AuthService } from '../../../auth/services/auth.service';
import { BanksService } from '../../../banks/services/banks.service';
import { SupervisorsService } from '../../../supervisors/services/supervisors.service';
import { BankSummaryDto } from '../../../../core/data/new-dtos/bank.dtos';
import { FormsModule } from '@angular/forms';


@Component({
    selector: 'app-insurance-types-list',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        SharedModule,
        TableComponent,
        TableColumnDirective,
        TableContainerComponent,
    ],
    templateUrl: './insurance-types-list.component.html',
    styleUrls: ['./insurance-types-list.component.scss'],
    providers: [SwalService],
})
export class InsuranceTypesListComponent implements OnInit {
    insuranceTypes: InsuranceTypeDto[] = [];
    banks: BankSummaryDto[] = [];
    selectedBankId: number | null = null;

    tableHeaders: TableHeader[] = [
        { attribute: 'name', label: 'Nombre' },
        { attribute: 'category', label: 'Categoría' },
        { attribute: 'minAge', label: 'Edad Mín.' },
        { attribute: 'maxAge', label: 'Edad Máx.' },
        { attribute: 'familyAllowed', label: 'Familiar' },
        { attribute: 'actions', label: 'Acciones' },
    ];

    constructor(
        private swal: SwalService,
        private insuranceTypesService: InsuranceTypesService,
        private router: Router,
        private auth: AuthService,
        private banksService: BanksService,
        private supervisorsService: SupervisorsService,
    ) { }

    get isSuperUser() {
        return this.auth.isSuperUser;
    }

    get isSupervisor() {
        return this.auth.isSupervisor;
    }

    get isRegional() {
        return this.auth.isRegional;
    }

    get showBankFilter() {
        return this.isSuperUser || this.isSupervisor;
    }

    get canManagePlans() {
        return this.isSuperUser || this.isSupervisor || this.auth.isGlobal;
    }

    /** Roles the form admits at all (planViewerGuard), even if only readonly. */
    get canViewReadonly() {
        return this.isSupervisor || this.auth.isGlobal || this.isRegional || this.auth.isManager;
    }

    /** Colonial products (no bank) belong to the shared catalog: superuser-only. */
    canEditProduct(bankId: number | null | undefined): boolean {
        if (this.isSuperUser) return true;
        return this.canManagePlans && bankId != null;
    }

    async ngOnInit() {
        await this.auth.profileReady();
        if (this.showBankFilter) {
            await this.loadBanks();
        }
        await this.refresh();
    }

    async loadBanks() {
        try {
            if (this.isSupervisor && this.auth.azureId) {
                // Supervisors only see the banks derived from their branch assignments
                const branches = await lastValueFrom(
                    this.supervisorsService.getBranches(this.auth.azureId),
                );
                this.banks = [...new Map(branches.map((b) => [b.bank.id, b.bank])).values()];
            } else {
                this.banks = await lastValueFrom(this.banksService.getBanks());
            }
        } catch {
            this.banks = [];
        }
    }

    async onBankFilterChange() {
        await this.refresh();
    }

    async refresh() {
        this.swal.showLoading('Cargando productos...');
        try {
            const bankId = this.selectedBankId ?? undefined;
            this.insuranceTypes = await lastValueFrom(
                this.insuranceTypesService.getAll(bankId),
            );
        } catch (e) {
            this.insuranceTypes = [];
        } finally {
            this.swal.dismiss();
        }
    }

    async onReorder(reordered: InsuranceTypeDto[]) {
        const previousOrder = this.insuranceTypes;
        this.insuranceTypes = reordered;
        try {
            await lastValueFrom(
                this.insuranceTypesService.reorder(reordered.map((t) => t.id)),
            );
        } catch (e) {
            this.insuranceTypes = previousOrder;
            this.swal.error('No se pudo guardar el nuevo orden.');
        }
    }

    navigateToCreate() {
        this.router.navigate(['/insurance-types/new']);
    }

    navigateToEdit(id: number) {
        this.router.navigate(['/insurance-types', id, 'edit']);
    }

    async deleteInsuranceType(id: number) {
        const { isConfirmed } = await this.swal.confirm(
            'Esta acción no se puede deshacer.',
            '¿Eliminar producto?',
            { confirmButtonText: 'Sí, eliminar' }
        );

        if (!isConfirmed) return;

        this.swal.showLoading('Eliminando...');
        try {
            await lastValueFrom(this.insuranceTypesService.delete(id));
            this.insuranceTypes = this.insuranceTypes.filter((t) => t.id !== id);
            await this.swal.success('Producto eliminado.');
        } catch (e) {
            this.swal.error('No se pudo eliminar el producto.');
        } finally {
            this.swal.dismiss();
        }
    }

    displayCategory(category: string): string {
        switch (category) {
            case 'Life': return 'Vida';
            case 'Health': return 'Salud';
            case 'Accident': return 'Accidentes';
            case 'Other': return 'Otro';
            default: return category;
        }
    }

    displayBoolean(value: boolean): string {
        return value ? 'Sí' : 'No';
    }
}
